import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Stack,
  InputAdornment, OutlinedInput, LinearProgress, Skeleton
} from '@mui/material';
import {
  Atom, Orbit, Flame, Sparkles, Cpu, Radio, Microscope,
  Search, Lock, Unlock, ArrowRight, Rocket, Target, FlaskConical
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import EnrollmentCodeModal from '../../components/common/EnrollmentCodeModal';
import { useAuth } from '../../context/AuthContext';
import { EnrollmentApi } from '../../services/api';

const BG = '#09090b';
const CARD_SX = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px',
};
const T_PRIMARY = '#fafafa';
const T_SECONDARY = '#a1a1aa';
const T_MUTED = '#71717a';

const LABS = [
  {
    id: 'physics', title: 'Engineering Physics Playground', subtitle: 'Physics you can fiddle with',
    category: 'physics', difficulty: 'Foundational', duration: '6 benches',
    icon: Atom, color: '#a78bfa', gradient: 'linear-gradient(135deg,#a78bfa,#6366f1)',
    path: '/virtuallabs/physics', modules: 6, available: true,
    desc: 'Forces, energy, spin, shake, flow, heat. Built so engineers enjoy physics — and so every generated course can drop you onto a bench.',
    formula: 'ΣF = ma · E = PE + KE',
    topics: ['Forces','Energy','Rotation','Vibration','Fluids','Heat'],
  },
  {
    id: 'semicon-mech', title: 'Semiconductor Mechanical Studio', subtitle: 'Packages that fight back',
    category: 'engineering', difficulty: 'Intermediate', duration: '3 benches',
    icon: Cpu, color: '#22d3ee', gradient: 'linear-gradient(135deg,#22d3ee,#6366f1)',
    path: '/virtuallabs/semicon-mech', modules: 3, available: true,
    desc: 'Warpage, peel, and a defence in front of a sceptical packaging senior. Mechanical engineering on a back-end line, with AI as examiner.',
    formula: 'δ = κL² / 8',
    topics: ['CTE mismatch','Warpage','Die attach','Peel / shear','AI defence'],
  },
  {
    id: 'orbital-mechanics', title: 'Orbital Mechanics Lab', subtitle: 'Navigate the Cosmos',
    category: 'aerospace', difficulty: 'Advanced', duration: '50+ hours',
    icon: Orbit, color: '#06b6d4', gradient: 'linear-gradient(135deg,#06b6d4,#0891b2)',
    path: '/virtuallabs/orbital', modules: 7, available: false,
    desc: 'Calculate trajectories, plan interplanetary missions, and understand the physics of spaceflight.',
    formula: 'v = √(GM/r)',
    topics: ["Kepler's Laws",'Hohmann Transfers','Gravity Assists','Orbital Maneuvers','Launch Windows'],
  },
  {
    id: 'propulsion', title: 'Rocket Propulsion Lab', subtitle: 'The Art of Controlled Explosions',
    category: 'aerospace', difficulty: 'Advanced', duration: '45+ hours',
    icon: Flame, color: '#f97316', gradient: 'linear-gradient(135deg,#f97316,#ea580c)',
    path: '/virtuallabs/propulsion', modules: 6, available: false,
    desc: 'Understand why rockets work from first principles. Design engines, calculate thrust, optimize for reusability.',
    formula: 'T = ṁ × Ve + (Pe - Pa) × Ae',
    topics: ['Thermodynamics','Nozzle Design','Combustion','Turbopumps','Materials Science'],
  },
  {
    id: 'quantum', title: 'Quantum Computing Lab', subtitle: 'Harness Quantum Weirdness',
    category: 'computing', difficulty: 'Expert', duration: '60+ hours',
    icon: Sparkles, color: '#ec4899', gradient: 'linear-gradient(135deg,#ec4899,#db2777)',
    path: '/virtuallabs/quantum', modules: 8, available: false,
    desc: 'Explore quantum mechanics applied to computation. Build intuition for superposition and entanglement.',
    formula: '|ψ⟩ = α|0⟩ + β|1⟩',
    topics: ['Qubits','Quantum Gates','Entanglement','Quantum Algorithms','Error Correction'],
  },
  {
    id: 'neural-engineering', title: 'Neural Engineering Lab', subtitle: 'Build Intelligence from Scratch',
    category: 'computing', difficulty: 'Advanced', duration: '55+ hours',
    icon: Cpu, color: '#14b8a6', gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)',
    path: '/virtuallabs/neural', modules: 7, available: false,
    desc: 'Build, train, and optimize AI systems. Understand how neural networks learn with mathematical rigor.',
    formula: 'y = σ(Wx + b)',
    topics: ['Perceptrons','Backpropagation','CNNs','Transformers','Optimization'],
  },
  {
    id: 'signals', title: 'Signals & Systems Lab', subtitle: 'The Language of Engineering',
    category: 'engineering', difficulty: 'Intermediate', duration: '40+ hours',
    icon: Radio, color: '#6366f1', gradient: 'linear-gradient(135deg,#6366f1,#4f46e5)',
    path: '/virtuallabs/signals', modules: 5, available: false,
    desc: 'Master the mathematical tools that underpin modern engineering — from Fourier to control systems.',
    formula: 'X(f) = ∫x(t)e^(-j2πft)dt',
    topics: ['Fourier Analysis','Laplace Transform','Filtering','Control Theory','Feedback Systems'],
  },
  {
    id: 'materials', title: 'Materials Science Lab', subtitle: 'Engineer at the Atomic Scale',
    category: 'engineering', difficulty: 'Advanced', duration: '45+ hours',
    icon: Microscope, color: '#84cc16', gradient: 'linear-gradient(135deg,#84cc16,#65a30d)',
    path: '/virtuallabs/materials', modules: 6, available: false,
    desc: 'Understand why materials behave as they do. From crystal structures to composite design.',
    formula: 'σ = Eε',
    topics: ['Crystal Structure','Phase Diagrams','Mechanical Properties','Composites','Fatigue & Fracture'],
  },
];

const CATEGORIES = [
  { id: 'all', label: 'All Labs' },
  { id: 'physics', label: 'Physics' },
  { id: 'mathematics', label: 'Mathematics' },
  { id: 'aerospace', label: 'Aerospace' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'computing', label: 'Computing' },
];

const DIFF_COLOR = { Foundational: '#10b981', Intermediate: '#f59e0b', Advanced: '#ef4444', Expert: '#8b5cf6' };

const LabCard = ({ lab, hasAccess, onEnter }) => {
  const IconComp = lab.icon;
  const diffColor = DIFF_COLOR[lab.difficulty] || T_MUTED;

  return (
    <Card sx={{
      ...CARD_SX, height: '100%', position: 'relative', overflow: 'hidden',
      opacity: lab.available ? 1 : 0.6,
      transition: 'border-color .2s, transform .2s',
      '&:hover': lab.available ? { borderColor: lab.color + '55', transform: 'translateY(-4px)' } : {},
    }}>
      {/* Top gradient bar */}
      <Box sx={{ height: 3, background: lab.available ? lab.gradient : 'rgba(255,255,255,0.08)' }} />

      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: 'calc(100% - 3px)' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2, background: lab.available ? lab.gradient : 'rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconComp size={26} color="#fff" />
          </Box>
          <Stack spacing={0.5} alignItems="flex-end">
            <Chip label={lab.difficulty} size="small"
              sx={{ bgcolor: diffColor + '22', color: diffColor, fontSize: 10, height: 20, fontWeight: 600 }} />
            {!lab.available && (
              <Chip label="Coming Soon" size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: T_MUTED, fontSize: 10, height: 20 }} />
            )}
          </Stack>
        </Box>

        {/* Info */}
        <Typography variant="subtitle1" fontWeight={700} color={T_PRIMARY} mb={0.3}>{lab.title}</Typography>
        <Typography variant="caption" color={lab.color} fontWeight={500} mb={1}>{lab.subtitle}</Typography>
        <Typography variant="caption" color={T_SECONDARY} lineHeight={1.6} mb={2}
          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {lab.desc}
        </Typography>

        {/* Formula */}
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 1, p: 1, mb: 2, border: '1px solid rgba(255,255,255,0.07)' }}>
          <Typography variant="caption" color={lab.color} fontFamily="monospace">{lab.formula}</Typography>
        </Box>

        {/* Meta */}
        <Stack direction="row" gap={1.5} mb={2} flexWrap="wrap">
          <Typography variant="caption" color={T_MUTED}>📚 {lab.modules} modules</Typography>
          <Typography variant="caption" color={T_MUTED}>⏱ {lab.duration}</Typography>
        </Stack>

        {/* Topics */}
        <Stack direction="row" gap={0.5} flexWrap="wrap" mb={2.5}>
          {lab.topics.slice(0, 3).map(t => (
            <Chip key={t} label={t} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: T_MUTED, fontSize: 10, height: 20 }} />
          ))}
          {lab.topics.length > 3 && (
            <Chip label={'+' + (lab.topics.length - 3)} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: T_MUTED, fontSize: 10, height: 20 }} />
          )}
        </Stack>

        {/* CTA */}
        <Box sx={{ mt: 'auto' }}>
          <Button
            fullWidth variant="contained"
            disabled={!lab.available}
            onClick={() => onEnter(lab)}
            startIcon={lab.available ? (hasAccess ? <Unlock size={16} /> : <Lock size={16} />) : null}
            endIcon={lab.available ? <ArrowRight size={16} /> : null}
            sx={{
              borderRadius: 2, fontWeight: 600, textTransform: 'none',
              background: lab.available ? lab.gradient : 'rgba(255,255,255,0.06)',
              color: lab.available ? '#fff' : T_MUTED,
              '&:hover': lab.available ? { opacity: 0.9, transform: 'none' } : {},
              '&:disabled': { background: 'rgba(255,255,255,0.06)', color: T_MUTED }
            }}
          >
            {!lab.available ? 'Coming Soon' : hasAccess ? 'Enter Lab' : 'Enter Lab'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const VirtualLabs = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [pendingLabId, setPendingLabId] = useState('');
  const [pendingLabTitle, setPendingLabTitle] = useState('');
  const [pendingLabPath, setPendingLabPath] = useState('');
  const [labAccess, setLabAccess] = useState({});

  useEffect(() => {
    const loadLabAccess = async () => {
      if (!isAuthenticated) return;
      const userId = user?._id || user?.id || 'anonymous';
      const storageKey = 'demoEnrollments_' + userId;
      const demoEnrollments = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const access = {};
      demoEnrollments.forEach(enrollment => {
        if (enrollment.labAccess && Array.isArray(enrollment.labAccess)) {
          enrollment.labAccess.forEach(lab => { access[lab.labId] = true; });
        }
      });
      if (token) {
        try {
          const semiconAccess = await EnrollmentApi.checkLabAccess(token, 'semicon-mech');
          if (semiconAccess.hasAccess) access['semicon-mech'] = true;
        } catch (err) {
          console.error('Lab access check error:', err);
        }
        try {
          const physicsAccess = await EnrollmentApi.checkLabAccess(token, 'physics');
          if (physicsAccess.hasAccess) access['physics'] = true;
        } catch (err) {
          console.error('Lab access check error:', err);
        }
      }
      setLabAccess(access);
    };
    loadLabAccess();
  }, [isAuthenticated, token, user]);

  const handleLabClick = (lab) => {
    if (!lab.available) return;
    if (!isAuthenticated) {
      alert('Please log in first. Then enroll in a course to receive an access code for virtual labs.');
      navigate('/login');
      return;
    }
    if (lab.id === 'physics' || labAccess[lab.id]) {
      navigate(lab.path);
    } else {
      setPendingLabId(lab.id);
      setPendingLabTitle(lab.title);
      setPendingLabPath(lab.path);
      setShowCodeModal(true);
    }
  };

  const handleValidateCode = async (code) => {
    const result = await EnrollmentApi.validateCode(token, code);
    if (result.success) {
      const enrollment = result.data.enrollment;
      const hasAccess = enrollment.labAccess.some(la => la.labId === pendingLabId);
      if (hasAccess) {
        setLabAccess(prev => ({ ...prev, [pendingLabId]: true }));
        setShowCodeModal(false);
        navigate(pendingLabPath);
      } else {
        throw new Error('This enrollment code does not grant access to this lab');
      }
    }
  };

  const filtered = LABS.filter(lab => {
    const matchesCat = selectedCategory === 'all' || lab.category === selectedCategory;
    const matchesSearch = !searchQuery ||
      lab.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lab.desc.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const availableCount = LABS.filter(l => l.available).length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, color: T_PRIMARY }}>
      <Header />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 8, md: 11 }, px: { xs: 3, md: 6 }, overflow: 'hidden',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.18) 0%, transparent 70%)' }}>
        <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Chip
            icon={<Rocket size={12} />}
            label="First Principles Engineering"
            sx={{ bgcolor: 'rgba(139,92,246,0.15)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)', mb: 3 }}
          />
          <Typography variant="h2" fontWeight={900} color={T_PRIMARY} lineHeight={1.1} mb={1.5}
            sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
            Virtual Labs —{' '}
            <Box component="span" sx={{ background: 'linear-gradient(135deg,#8b5cf6,#06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Hands-on Science
            </Box>
          </Typography>
          <Typography variant="body1" color={T_SECONDARY} mb={4} sx={{ maxWidth: 540, mx: 'auto' }}>
            World-class virtual laboratories designed to help you understand the universe from first principles. No memorization — just deep understanding.
          </Typography>
          <Stack direction="row" gap={4} justifyContent="center" flexWrap="wrap">
            {[
              { value: LABS.length, label: 'Labs' },
              { value: '300+', label: 'Hours' },
              { value: availableCount, label: 'Live Now' },
            ].map(s => (
              <Box key={s.label} sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={800}
                  sx={{ background: 'linear-gradient(135deg,#8b5cf6,#06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color={T_MUTED}>{s.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>

        {/* Search + filters */}
        <OutlinedInput
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search labs..."
          fullWidth
          startAdornment={<InputAdornment position="start"><Search size={18} color={T_MUTED} /></InputAdornment>}
          sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.03)', color: T_PRIMARY, borderRadius: 2,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8b5cf6' } }}
        />
        <Stack direction="row" gap={1} flexWrap="wrap" mb={4}>
          {CATEGORIES.map(cat => (
            <Chip key={cat.id} label={cat.label} onClick={() => setSelectedCategory(cat.id)}
              sx={{ cursor: 'pointer',
                bgcolor: selectedCategory === cat.id ? '#8b5cf6' : 'rgba(255,255,255,0.05)',
                color: selectedCategory === cat.id ? '#fff' : T_SECONDARY,
                border: selectedCategory === cat.id ? 'none' : '1px solid rgba(255,255,255,0.08)',
                '&:hover': { bgcolor: selectedCategory === cat.id ? '#7c3aed' : 'rgba(255,255,255,0.09)' },
                transition: 'all .15s' }} />
          ))}
        </Stack>

        {/* Labs grid */}
        <Grid container spacing={2.5} sx={{ mb: 6 }}>
          {filtered.map(lab => (
            <Grid item xs={12} sm={6} md={4} key={lab.id}>
              <LabCard lab={lab} hasAccess={labAccess[lab.id]} onEnter={handleLabClick} />
            </Grid>
          ))}
          {filtered.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h1" sx={{ mb: 2 }}>🔭</Typography>
                <Typography variant="body1" color={T_MUTED}>No labs match your search.</Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Access Key section */}
        <Box sx={{ ...CARD_SX, p: { xs: 3, md: 4 }, background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(6,182,212,0.04))' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: 'rgba(139,92,246,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Unlock size={24} color="#c4b5fd" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" fontWeight={700} color={T_PRIMARY} mb={0.5}>Lab Access System</Typography>
              <Typography variant="body2" color={T_SECONDARY} mb={1.5} lineHeight={1.7}>
                Virtual labs are included with course enrollment. When you enroll in a QoreNova course, you'll receive an access code that unlocks the corresponding lab. Enter the code to gain instant access.
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {['Enroll in a course', 'Receive your access code', 'Unlock the lab', 'Learn by doing'].map((step, i) => (
                  <Chip key={step}
                    label={(i + 1) + '. ' + step}
                    size="small"
                    sx={{ bgcolor: 'rgba(139,92,246,0.12)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.2)', fontSize: 11 }} />
                ))}
              </Stack>
            </Box>
            <Button variant="outlined" onClick={() => navigate('/courses')}
              endIcon={<ArrowRight size={16} />}
              sx={{ borderColor: 'rgba(139,92,246,0.3)', color: '#c4b5fd', borderRadius: 2, flexShrink: 0,
                '&:hover': { borderColor: '#8b5cf6', bgcolor: 'rgba(139,92,246,0.08)' } }}>
              Browse Courses
            </Button>
          </Box>
        </Box>
      </Box>

      {showCodeModal && (
        <EnrollmentCodeModal
          isOpen={showCodeModal}
          onClose={() => setShowCodeModal(false)}
          onValidate={handleValidateCode}
          labName={pendingLabTitle}
        />
      )}

      <Footer />
    </Box>
  );
};

export default VirtualLabs;
